import axiosInstance from "../common/axiosInstance";
import {
  UseQueryResult,
  useQuery,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";

export const ACCOUNT_PATHS = Object.freeze({
  ACCOUNT_TOPICS: "/account-topics",
  SEVENG_BOOKING_LINK: "/calendar-booking-url",
  CALENDAR_AVAILABILITY: "/calendar-availability",
  USER_SETTINGS: "/user-settings-update",
  GET_USER_LEVEL_SETTINGS: "/user-level-settings",
  GET_AUTHORIZE_URL: "calendar/oauth/authorize",
  USER_NOTIFICATION: "/user-notification-settings",
  GET_BOT_SETTINGS: "/bot-settings",
  POST_BOT_SETTINGS: "/upsert-bot-settings",
  GET_ACCOUNT_CONFIG: "/account-config",
  POST_ACCOUNT_CONFIG: "/upsert-account-config",
  GET_ACTIVE_ACCOUNTS: "/active-accounts",
  GET_ORG_MANAGEMENT: "/org-details",
  GET_SETTING_ORGANIZATION: "/setting-organization/update",
  GET_USER_PREFERENCE: "/user-preferences",
  GET_USER_HIEARCHY_UPDATE: "/user/heirarachy-update",
  GET_VIDEO_GUIDES_URLS: "/video-presigned-urls"
});

interface GetUserLevelSettings {
  employeeEmail: string;
  fullName?: string;
  syncEnabled?: boolean;
  botSchedulingDisabled?: boolean;
  limit?: number;
  offset?: number;
  sortOrder?: "asc" | "desc";
}
interface UserSettingsUpdatePayload {
  userSettings: {
    profileOrgRelationshipId: string;
    isBotSchedulingDisabled?: boolean;
    isSyncEnabled?: boolean;
    isEmailSyncEnabled?: boolean;
  }[];
}

interface UserNotificationSettingsPayload {
  timeZone: string;
  frequency: "daily" | "weekly" | "none";
  frequencyDay?: string;
  timePreference: {
    [time: string]: boolean;
  };
  notificationType: {
    completedTask: boolean;
    overDueTask: boolean;
    upcomingTask: boolean;
  };
}

interface AccountConfigPayload {
  accountId: string;
  config: Record<string, any>;
}

interface GetOrgManagementParams {
  offset: number;
  limit: number;
  sortOrder?: "asc" | "desc";
  sortBy?: string;
  searchDomain: string;
  accountName?: string;
  relationshipType?: string;
  accountManagers?: string[];
  onboardedStartDate?: string;
  onboardedEndDate?: string;
  offboardedStartDate?: string;
  offboardedEndDate?: string;
  clientType?: string[];
}

interface GetOrgManagementPayload {
  orgId: string;
  mrr?: number;
  relationshipType?: string;
  accountName?: string;
  accountManager?: string;
  onboardedDate?: string | null;
  offboardedDate?: string | null;
  fieldsToClear?: string[];
  subOrgId: string;
  clientType?: string;
}

interface GetUserPreferenceParams {
  employeePorId: string;
}
type TaskColumns = {
  title?: boolean;
  status?: boolean;
  assignee?: boolean;
  due_date?: boolean;
  priority?: boolean;
  serialNo?: boolean;
  coach?: boolean;
  tags?: boolean;
  subscribers?: boolean;
  execSponsor?: boolean;
  account?: boolean;
};
interface UpdateUserPreferencePayload {
  table_preferences?: {
    tasks?: {
      columns?: TaskColumns;
      default_time_period?: string;
    };
  };
  salesforce_report?: Record<string, any>;
  customer_preferences?: Record<string, any>;
}

type UpdateUserPreferenceVariables = {
  params: GetUserPreferenceParams;
  payload: UpdateUserPreferencePayload;
};
const fetchAccountTopics = async (): Promise<any> => {
  const response = await axiosInstance.get(ACCOUNT_PATHS.ACCOUNT_TOPICS);
  return response;
};

const postAccountTopic = async (data: {
  topicName: string;
  description: string;
  isForUpdate: boolean;
}): Promise<any> => {
  const response = await axiosInstance.post(ACCOUNT_PATHS.ACCOUNT_TOPICS, data);
  return response;
};
  
const updateAccountTopic = async (data: {
  topicId: string;
  topicName: string;
  description: string;
  isForUpdate: boolean;
}): Promise<any> => {
  const response = await axiosInstance.post(ACCOUNT_PATHS.ACCOUNT_TOPICS, data);
  return response;
};

const deleteAccountTopic = async (topicId: string): Promise<any> => {
    const response = await axiosInstance.delete(
      `${ACCOUNT_PATHS.ACCOUNT_TOPICS}?topicId=${topicId}`
    );
    return response;
  };

  const getCalendarAvailability = async (): Promise<any> => {
    const response = await axiosInstance.get(ACCOUNT_PATHS.CALENDAR_AVAILABILITY);
    return response;
  };

  const updateAvailability = async (data: {
    updates: {day_of_week: string; start_time: string; end_time: string}[];
    deletes: {day_of_week: string; start_time: string; end_time: string}[];
    timeZone: string;
  }): Promise<any> => {
    const response = await axiosInstance.post(
      `${ACCOUNT_PATHS.CALENDAR_AVAILABILITY}`,
      {
        updates: data.updates,
        deletes: data.deletes,
        timeZone: data.timeZone,
      }
    );
    return response.data;
  };

  const useGetCalendarAvailability = (enabled: boolean): UseQueryResult<any, any> => {
    return useQuery({
      queryKey: ["get_calendar_availability"],
      queryFn: getCalendarAvailability,
      enabled,
      retry: 0,
    });
  }

  const useUpdateCalendarAvailability = (
    handlerFunctions?: any
  ): UseMutationResult<
    any,
    any,
    { updates: {day_of_week: string; start_time: string; end_time: string}[]; 
      deletes: {day_of_week: string; start_time: string; end_time: string}[];
      timeZone: string 
    }> => {
    return useMutation({
      mutationKey: ["update_calendar_availability"],
      mutationFn: updateAvailability,
      ...handlerFunctions,
    });
  };

const useGetAccountTopics = (enabled: boolean): UseQueryResult<any, any> => {
  return useQuery({
    queryKey: ["get_account_topics"],
    queryFn: fetchAccountTopics,
    enabled,
    retry: 0,
  });
};

const usePostAccountTopic = (
  handlerFunctions?: any
): UseMutationResult<
  any,
  any,
  {
    topicName: string;
    description: string;
    isForUpdate: boolean;
  }
> => {
  return useMutation({
    mutationKey: ["post_account_topic"],
    mutationFn: postAccountTopic,
    ...handlerFunctions,
  });
};

const useUpdateAccountTopic = (
    handlerFunctions?: any
  ): UseMutationResult<
    any,
    any,
    {
      topicId: string;
      topicName: string;
      description: string;
      isForUpdate: boolean;
    }
  > => {
    return useMutation({
      mutationKey: ["update_account_topic"],
      mutationFn: updateAccountTopic,
      ...handlerFunctions,
    });
  };

  const useDeleteAccountTopic = (
    handlerFunctions?: any
  ): UseMutationResult<any, any, { topicId: string }> => {
    return useMutation({
      mutationKey: ["delete_account_topic"],
      mutationFn: ({ topicId }) => deleteAccountTopic(topicId),
      ...handlerFunctions,
    });
  };

  const getBookingUrl = async (): Promise<any> => {
    const response = await axiosInstance.get(ACCOUNT_PATHS.SEVENG_BOOKING_LINK);
    return response;
  };

  const useGetBookingUrl = (enabled: boolean): UseQueryResult<any, any> => {
    return useQuery({
      queryKey: ["get_bookings_url"],
      queryFn: getBookingUrl,
      enabled,
      retry: 0,
    });
  };

  const updateUserSettings = async (data: UserSettingsUpdatePayload): Promise<any> => {
    const response = await axiosInstance.post(
      ACCOUNT_PATHS.USER_SETTINGS,
      data
    );
    return response;
  };

  const useUpdateUserSettings = (
    handlerFunctions?: any
  ): UseMutationResult<any, any, UserSettingsUpdatePayload> => {
    return useMutation({
      mutationKey: ["update_user_settings"],
      mutationFn: updateUserSettings,
      ...handlerFunctions,
    });
  };

  const getAuthorizeUrlFetch = async (params: {
    userId: string;
    employeeEmail: string;
  }): Promise<any> => {
    const baseUrl = import.meta.env.VITE_JAVA_SERVICE_ACCOUNTS_BASE;
  
    const queryParams = new URLSearchParams({
      userId: params.userId,
      employeeEmail: params.employeeEmail,
    });
  
    const apiUrl = `${baseUrl}${ACCOUNT_PATHS.GET_AUTHORIZE_URL}?${queryParams.toString()}`;
  
    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        Accept: "*/*",
        "Content-Type": "application/json",
      },
    });
  
    if (!response.ok) {
      throw new Error(`Failed to fetch authorize URL. Status: ${response.status}`);
    }
  
    const data = await response.json();
    return data;
  };  
  
  const useGetAuthorizeUrl = (
    params: { userId: string; employeeEmail: string } | undefined,
    enabled: boolean
  ): UseQueryResult<any, any> => {
    return useQuery({
      queryKey: ["get_authorize_url", params],
      queryFn: () => getAuthorizeUrlFetch(params!),
      enabled: !!params && enabled,
      retry: 0,
    });
  };
  
  const getUserLevelSettings = async (
    params: GetUserLevelSettings
  ): Promise<any> => {
    const response = await axiosInstance.post(ACCOUNT_PATHS.GET_USER_LEVEL_SETTINGS, 
      {
        ...(params.fullName && {
        fullName: params.fullName,
      }),
      ...(params.syncEnabled != null && { syncEnabled: params.syncEnabled }),
      ...(params.botSchedulingDisabled != null && { botSchedulingDisabled: params.botSchedulingDisabled }),
      },{
      params: {
        offset: params.offset,
        limit: params.limit,
        employeeEmail: params.employeeEmail,
        sortOrder: params.sortOrder,
      },
    });
    return response.data;
  };

  const useGetUserLevelSettings = (params: GetUserLevelSettings,
    enabled: boolean
  ): UseQueryResult<any, any> => {
    const stringifiedParams = JSON.stringify(params);
    return useQuery({
      queryKey: ["get_user_level_settings", stringifiedParams],
      queryFn: () => getUserLevelSettings(params),
      enabled,
      retry: 0,
    });
  };

  const getUserNotificationSettings = async(): Promise<any> => {
    const response = await axiosInstance.get(ACCOUNT_PATHS.USER_NOTIFICATION);
    return response.data;
  };

  const useGetUserNotificationSettings = (
    enabled:boolean
  ): UseQueryResult<any, any> => {
    return useQuery({
      queryKey: ["get_user_notification_settings"],
      queryFn: () => getUserNotificationSettings(),
      enabled,
      retry: 0,
    })
  };
  
  const updateUserNotificationSettings = async ( data: UserNotificationSettingsPayload): Promise<any> => {
    const response = await axiosInstance.post(
      ACCOUNT_PATHS.USER_NOTIFICATION,
      data
    );
    return response.data;
  };

  const useUpdateUserNotificationSettings = (
    handlerFunctions?: any
  ): UseMutationResult<any, any, UserNotificationSettingsPayload> => {
    return useMutation({
      mutationKey: ["update_user_notification_settings"],
      mutationFn: (data: UserNotificationSettingsPayload) =>
        updateUserNotificationSettings(data),
      ...handlerFunctions,
    });
  };
  const getBotSettings = async (): Promise<any> => {
    const response = await axiosInstance.get(
      ACCOUNT_PATHS.GET_BOT_SETTINGS
    );
    return response.data;
  };

  const useGetBotSettings = (
    enabled: boolean
  ): UseQueryResult<any, any> => {
    return useQuery({
      queryKey: ["get_bot_settings"],
      queryFn: getBotSettings,
      enabled,
      retry: 0,
    });
  };

  const upsertBotSettings = async (
    botSettingType: string,
    profileOrgId: string,
  ): Promise<any> => {
    const response = await axiosInstance.post(
      `${ACCOUNT_PATHS.POST_BOT_SETTINGS}`,
      {},
      {
        params: {
          botSettingType,
          profileOrgId,
        },
      }
    );
    return response.data;
  };

  const useUpsertBotSettings = (
    handlerFunctions?: any
  ): UseMutationResult<
    any,
    any,
    {
      botSettingType: string;
      profileOrgId: string;
    }
  > => {
    return useMutation({
      mutationKey: ["upsert_bot_settings"],
      mutationFn: ({ botSettingType, profileOrgId }) =>
        upsertBotSettings(botSettingType, profileOrgId),
      ...handlerFunctions,
    });
  };

  const getAccountConfig = async (
  params: { accountId: string }
  ): Promise<any> => {
    const response = await axiosInstance.get(
      ACCOUNT_PATHS.GET_ACCOUNT_CONFIG,
      {
        params: { accountId: params.accountId },
      }
    );

    return response;
  };

  const useGetAccountConfig = (
    params: { accountId: string },
    enabled: boolean
  ): UseQueryResult<any, any> => {
    return useQuery({
      queryKey: ["get_account_config", params.accountId],
      queryFn: () => getAccountConfig({ accountId: params.accountId }),
      enabled: !!params.accountId && enabled,
      retry: 0,
    });
  };

  const upsertAccountConfig = async (
    data: AccountConfigPayload
  ): Promise<any> => {
    const response = await axiosInstance.post(
      ACCOUNT_PATHS.POST_ACCOUNT_CONFIG,
      data
    );

    return response.data;
  };

  const useUpsertAccountConfig = (
    handlerFunctions?: any
  ): UseMutationResult<any, any, AccountConfigPayload> => {
    return useMutation({
      mutationKey: ["upsert_account_config"],
      mutationFn: upsertAccountConfig,
      ...handlerFunctions,
    });
  };

  const getActiveAccounts = async (): Promise<any> => {
    const response = await axiosInstance.get(ACCOUNT_PATHS.GET_ACTIVE_ACCOUNTS);
    return response;
  };

  const useGetActiveAccounts = (
    enabled: boolean
  ): UseQueryResult<any, any> => {
    return useQuery({
      queryKey: ["get_active_accounts"],
      queryFn: getActiveAccounts,
      enabled,
      retry: 0,
    });
  };

  const getOrgManagement = async (params: GetOrgManagementParams): Promise<any> => {
    const response = await axiosInstance.post(ACCOUNT_PATHS.GET_ORG_MANAGEMENT, 
      {
      ...(params.searchDomain && { searchDomain: params.searchDomain }),
      ...(params.accountName && { accountName: params.accountName }),
      ...(params.relationshipType && { relationshipType: params.relationshipType }),
      ...(params.accountManagers && { accountManagers: params.accountManagers }),
      ...(params.onboardedStartDate && { onboardedStartDate: params.onboardedStartDate }),
      ...(params.onboardedEndDate && { onboardedEndDate: params.onboardedEndDate }),
      ...(params.offboardedStartDate && { offboardedStartDate: params.offboardedStartDate }),
      ...(params.offboardedEndDate && { offboardedEndDate: params.offboardedEndDate }),
      ...(params.clientType && { clientType : params.clientType }),
    },
    {
      params: {
        offset: params.offset,
        limit: params.limit,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
      }
    },
  );
    return response.data;
  };

  const useGetOrgManagement = (
    params: GetOrgManagementParams,
    enabled: boolean
  ): UseQueryResult<any, any> => {
    return useQuery({
      queryKey: ["get_org_management", params],
      queryFn: () => getOrgManagement(params),
      enabled,
      retry: 0,
    })
  };

  const updateOrgManagement = async (
    payload: GetOrgManagementPayload
  ): Promise<any> => {
    const response = await axiosInstance.post(
      ACCOUNT_PATHS.GET_SETTING_ORGANIZATION,
      payload
    );

    return response.data;
  };

  const useUpdateOrgManagement = (
    handlerFunctions?: any
  ): UseMutationResult<
    any,
    any,
    GetOrgManagementPayload
  > => {
    return useMutation({
      mutationKey: ["update_Org_management"],
      mutationFn: updateOrgManagement,
      ...handlerFunctions,
    });
  };

  const getUserPrefernces = async(
    params: GetUserPreferenceParams
  ):Promise<any> => {
    const response = await axiosInstance.get(ACCOUNT_PATHS.GET_USER_PREFERENCE,{
      params: {
        employeePorId: params.employeePorId
      }
    });
    return response.data;
  };

  const useGetUserPreferences = (
    params: GetUserPreferenceParams,
    enabled: boolean
  ): UseQueryResult<any, any> => {
    return useQuery({
      queryKey: ["get_user_preferences", params],
      queryFn: () => getUserPrefernces(params),
      enabled,
      retry: 0,
    })
  };

  const updateUserPreferences = async (
    params: GetUserPreferenceParams,
    payload: UpdateUserPreferencePayload
  ): Promise<any> => {
    const response = await axiosInstance.post(
      ACCOUNT_PATHS.GET_USER_PREFERENCE,
      payload,
      {
        params: {
          employeePorId: params.employeePorId,
        },
      }
    );
    return response.data;
  };

  const useUpdateUserPreferences = () => {
    return useMutation({
      mutationFn: ({
        params,
        payload,
      }: {
        params: GetUserPreferenceParams;
        payload: UpdateUserPreferencePayload;
      }) => updateUserPreferences(params, payload),
    });
  };

  const updateUserHeirarchy = async (data: {
    employeeProfileId: string;
    managerProfileId?: string;
  }): Promise<any> => {
    const response = await axiosInstance.post(
      ACCOUNT_PATHS.GET_USER_HIEARCHY_UPDATE,
      data
    );
    return response.data;
  };

  const useUpdateUserHeirarchy = (
    handlerFunctions?: any
  ): UseMutationResult<
    any,
    any,
    {
      employeeProfileId: string;
      managerProfileId?: string;
    }
  > => {
    return useMutation({
      mutationKey: ["update_user_heirarchy"],
      mutationFn: (data: { employeeProfileId: string; managerProfileId?: string }) =>
        updateUserHeirarchy(data),
      ...handlerFunctions,
    });
  };

  const getVideoPresignedUrls = async (): Promise<any> => {
    const response = await axiosInstance.get(ACCOUNT_PATHS.GET_VIDEO_GUIDES_URLS);
    return response;
  };

  const useGetVideoPresignedUrls = (
    enabled:boolean
  ): UseQueryResult<any, any> => {
    return useQuery({
      queryKey: ["get_video_presigned_urls"],
      queryFn: () => getVideoPresignedUrls(),
      enabled,
      retry: 0,
    })
  }
export interface IntegrationService {
  readonly useGetAccountTopics: (enabled: boolean) => UseQueryResult<any, any>;
  readonly usePostAccountTopic: (handlerFunctions?: any) => UseMutationResult<
    any,
    any,
    {
      topicName: string;
      description: string;
      isForUpdate: boolean;
    }
  >;
  readonly useUpdateAccountTopic: (handlerFunctions?: any) => UseMutationResult<
    any,
    any,
    {
      topicId: string;
      topicName: string;
      description: string;
      isForUpdate: boolean;
    }
  >;
  readonly useDeleteAccountTopic: (
    handlerFunctions?: any
  ) => UseMutationResult<any, any, { topicId: string }>;

  readonly useGetBookingUrl: (
    enabled: boolean) => UseQueryResult<any, any>;
  
  readonly useGetCalendarAvailability: (enabled: boolean) => UseQueryResult<any, any>;
  readonly useUpdateCalendarAvailability: (
    handlerFunctions?: any
  ) => UseMutationResult<any, any, { updates: any[]; deletes: any[]; timeZone: string;}>;


  readonly useUpdateUserSettings: (
    handlerFunctions?: any
  ) => UseMutationResult<any, any, UserSettingsUpdatePayload>;

  readonly useGetUserLevelSettings: (
    params: GetUserLevelSettings,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useGetAuthorizeUrl: (
    params: { userId: string; employeeEmail: string } | undefined,
    enabled: boolean
  ) => UseQueryResult<any, any>;  

  readonly useGetUserNotificationSettings: (
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useUpdateUserNotificationSettings: (
    handlerFunctions?: any
  ) => UseMutationResult<any, any, UserNotificationSettingsPayload>;

  readonly useGetBotSettings: (
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useUpsertBotSettings: (
    handlerFunctions?: any
  ) => UseMutationResult<any, any,
    {
      botSettingType: string;
      profileOrgId: string;
    } >;

  readonly useGetAccountConfig: (
    params: { accountId: string },
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useUpsertAccountConfig: (
    handlerFunctions?: any
  ) => UseMutationResult<any, any, AccountConfigPayload>;

  readonly useGetActiveAccounts: (
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useGetOrgManagement: (
    params: GetOrgManagementParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useUpdateOrgManagement: (
    handlerFunctions?: any
  ) => UseMutationResult<any, any, GetOrgManagementPayload>;

  readonly useGetUserPreferences: (
    params: GetUserPreferenceParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useUpdateUserPreferences: (
    handlerFunctions?: any
  ) => UseMutationResult<any, any, UpdateUserPreferenceVariables>;

    readonly useUpdateUserHeirarchy: (
      handlerFunctions?: any
    ) => UseMutationResult<any, any, {
      employeeProfileId: string;
      managerProfileId?: string;
    }>;

    readonly useGetVideoPresignedUrls: (
      enabled: boolean
    ) => UseQueryResult<any, any>;
}

const IntegrationService: IntegrationService = Object.freeze({
  useGetAccountTopics,
  usePostAccountTopic,
  useUpdateAccountTopic,
  useDeleteAccountTopic,
  useGetBookingUrl,
  useGetCalendarAvailability,
  useUpdateCalendarAvailability,
  useUpdateUserSettings,
  useGetUserLevelSettings,
  useGetAuthorizeUrl,
  useGetUserNotificationSettings,
  useUpdateUserNotificationSettings,
  useGetBotSettings,
  useUpsertBotSettings,
  useGetAccountConfig,
  useUpsertAccountConfig,
  useGetActiveAccounts,
  useGetOrgManagement,
  useUpdateOrgManagement,
  useGetUserPreferences,
  useUpdateUserPreferences,
  useUpdateUserHeirarchy,
  useGetVideoPresignedUrls,
});

export default IntegrationService;
